import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import styled from "styled-components/macro";

import houseHoldDatabaseKeys from "HouseholdDatabase/API/keys";
// import { colors } from "UI/colors";
import validateDescription, {
  validatePublicName,
} from "Signups/FamilyRegistration/Shared/DragNDropCheckboxItem/validateDescription";
import ErrorMessage from "Signups/FamilyRegistration/Shared/ErrorMessage";
// import { FlexCenter } from "Signups/FamilyRegistration/Shared/StyledComponents";
// import DragNDropCheckboxItem from "Signups/FamilyRegistration/Shared/DragNDropCheckboxItem";
import useUpdateSetting from "Signups/FamilyRegistration/Shared/useUpdateSetting";
import { AnimatePresence, motion } from "framer-motion";

import FnrButton from "UI/FnrButton3/FnrButton";
import FnrIcon from "UI/FnrIcon";
import FnrInput from "UI/FnrInput";

export default function AddANewStatus({ setError, existingPublicNames }) {
  const [editing, setEditing] = useState(false);
  const [validationError, setValidationError] = useState();

  const [nameValue, setNameValue] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");

  const queryClient = useQueryClient();
  const {
    updateSetting,
    saving,
    error,
    setError: setUpdateError,
  } = useUpdateSetting();

  async function handleSave(e) {
    if (e) {
      e.preventDefault();
    }
    setError(null);
    setValidationError(null);
    setUpdateError(null);

    try {
      const validation = validateDescription(nameValue, "status");
      const publicNameValidation = validatePublicName({
        publicName: descriptionValue,
        text: "description for the registration page",
        existingPublicNames,
      });

      if (!validation.valid) {
        setValidationError(validation.error);
        return;
      }
      if (!publicNameValidation.valid) {
        setValidationError(publicNameValidation.error);
        return;
      }

      let publicName =
        descriptionValue.trim().length > 0 ? descriptionValue : nameValue;

      let postData = {
        setting: "membershipStatus",
        itemID: null,
        value: nameValue,
        action: "create",
        publicName,
      };

      await updateSetting(postData);
      setEditing(false);
      // invalidate household database cache so that the new status is available immediately
      queryClient.invalidateQueries(houseHoldDatabaseKeys.all());
    } catch (err) {
      setError(err);
    }
  }

  function handleCancel() {
    setEditing(false);
  }

  useEffect(() => {
    if (editing) {
      setNameValue("");
      setDescriptionValue("");
      setError(null);
      setValidationError(null);
      setUpdateError(null);
    }
  }, [editing, setError, setUpdateError]);

  return (
    <>
      <Padding />
      <AnimatePresence>
        {editing ? (
          <EditingWrapper
            onSubmit={handleSave}
            initial={{ opacity: 0, height: 0, scale: 0.9 }}
            animate={{ opacity: 1, height: "auto", scale: 1 }}
            exit={{ opacity: 0, height: 0, scale: 0.9 }}
            key="editing_form"
          >
            <EditingInside>
              <EditingTitle>
                <FnrIcon far fa-plus-circle /> Add a new registration status
              </EditingTitle>
              <InputWrapper>
                <FnrInput
                  type="text"
                  placeholder="Status Name"
                  disabled={saving}
                  autoFocus={true}
                  value={nameValue}
                  onChange={setNameValue}
                />
                <FnrInput
                  type="text"
                  placeholder="Text on Registration Page"
                  disabled={saving}
                  autoFocus={false}
                  value={descriptionValue}
                  onChange={setDescriptionValue}
                  style={{ marginTop: 12 }}
                  size="small"
                />
                <EditingButtonsHolder>
                  <FnrButton
                    onClick={handleCancel}
                    disabled={saving}
                    type="button"
                    aria-label="save new status"
                  >
                    Cancel
                  </FnrButton>

                  <FnrButton
                    type="submit"
                    color="green"
                    loading={saving}
                    aria-label="Exit new status editor"
                  >
                    Save
                  </FnrButton>
                </EditingButtonsHolder>
              </InputWrapper>
            </EditingInside>
          </EditingWrapper>
        ) : (
          <NewStatusContainer
            onClick={() => setEditing(true)}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
            key="new_status_button"
          >
            <NewStatusButton>
              <FnrIcon far fa-plus-circle />
              <div>Add a New Status</div>
            </NewStatusButton>
          </NewStatusContainer>
        )}
      </AnimatePresence>

      <ErrorMessage
        error={validationError || error?.message}
        marginLeft="0px"
        size="sm"
      />
    </>
  );
}

const NewStatusContainer = styled(motion.div)`
  overflow: hidden;
`;
const NewStatusButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding-top: 6px;
  cursor: pointer;
  padding-bottom: 4px;
`;

const EditingWrapper = styled(motion.form)`
  overflow: hidden;
`;
const EditingInside = styled.div`
  background: #f1f4f6;
  border-radius: 5px;
  padding: 10px;
`;
const EditingTitle = styled.div`
  font-size: 18px;
  margin-bottom: 12px;
  display: flex;
  gap: 10px;
  padding-left: 3px;
`;
const InputWrapper = styled.div``;

const EditingButtonsHolder = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
`;
const Padding = styled.div`
  height: 5px;
`;
